import {Mapper} from '@s-ui/domain'

/**
 * @implements Mapper
 * Use the raw response from the http repository to create a Seller Value Object.
 */
export class FromAdDetailsRawDataToSellerValueObjectMapper extends Mapper {
  constructor({
    config,
    fromDealerRawDataToDealerEntityMapper,
    getSellerRequestMapper,
    sellerValueObjectFactory,
    userEntityFactory
  }) {
    super()

    const {FUEL_TYPES} = config.get('advertising')

    this._config = config
    this._appName = this._config.get('APP_NAME')
    this._culture = this._config.get('DEFAULT_CULTURE')
    this._fromDealerRawDataToDealerEntityMapper = fromDealerRawDataToDealerEntityMapper
    this._fuelTypes = FUEL_TYPES
    this._getSellerRequestMapper = getSellerRequestMapper
    this._provinces = this._config.get('provinces')
    this._sellerValueObjectFactory = sellerValueObjectFactory
    this._transmissionTypes = config.get('search').transmissionTypes || []
    this._userEntityFactory = userEntityFactory
  }

  _getProvinceName = provinceId => {
    if (!provinceId) return
    const {label: provinceName} = this._provinces.find(province => String(province.id) === String(provinceId))
    return provinceName
  }

  _getTransmissionName = ({transmissionTypeId}) => {
    const [transmissionType] = this._transmissionTypes.filter(({id}) => Number(id) === Number(transmissionTypeId))
    return transmissionType ? transmissionType.label : undefined
  }

  map = async rawData => {
    const {ad} = rawData

    const {privateSeller = {}, professionalSeller = {}} = rawData

    const {additionalInfo: professionalSellerAdditionalInfo = {}, contact: professionalSellerContact} =
      professionalSeller
    const {services = []} = professionalSeller

    const dealerServices = services.map(({description, name, type, included}) => ({
      description,
      title: name,
      type,
      included
    }))

    const sellerRequestVO = this._getSellerRequestMapper.map({
      contractId: professionalSeller.dealerIdentity?.id || professionalSeller.id,
      userId: ad.privateSeller?.userId || privateSeller.userId
    })

    const {contractId, isDealer, isPrivate} = sellerRequestVO.toJSON()
    const hasRatings = professionalSeller?.ratings && Object.keys(professionalSeller.ratings).length

    const dealer =
      isDealer &&
      (await this._fromDealerRawDataToDealerEntityMapper.map({
        brandingActive: professionalSeller.brandingActive,
        showContactLogo: professionalSeller.showContactLogo,
        showDealerDirections: professionalSeller.showDealerDirections,
        contact: {
          phone: professionalSellerContact.phone1,
          hasCallTrackingService: professionalSellerContact.hasCallTrackingService,
          // Only an explicit `true` from the API offers WhatsApp; when the field does not travel, it is hidden.
          hasWhatsappProduct: professionalSellerContact.hasWhatsappProduct ?? false
        },
        externalContractId: professionalSeller.externalContractId,
        externalPageUrl: professionalSeller.externalPageUrl,
        hasFinancingProduct: Boolean(professionalSeller.hasFinancingProduct),
        hasReservation: professionalSeller.hasReservation,
        id: contractId,
        location: professionalSeller.location,
        logoUrl: professionalSeller.logoUrl,
        mainRentingServicesIncluded: professionalSeller.mainRentingServicesIncluded,
        name: professionalSeller.name,
        pack: professionalSeller.pack,
        services: dealerServices,
        ...(hasRatings && {ratings: professionalSeller.ratings}),
        stockPageUrl: professionalSeller.stockPageSlug,
        storeImageUrls: professionalSellerAdditionalInfo.storeImageUrls
      }))

    const user =
      isPrivate &&
      this._userEntityFactory({
        avatar: privateSeller.avatarUrl,
        name: privateSeller.name,
        phone: privateSeller.phone,
        province: this._getProvinceName(privateSeller.provinceId),
        userId: privateSeller.userId
      })

    return this._sellerValueObjectFactory({
      contractId,
      dealer: isDealer && dealer,
      isDealer,
      isPrivate,
      user: isPrivate && user
    })
  }
}
